import { createApiUrl } from '../api'

export interface AddressRequest {
  street: string
  city: string
  state: string
  postalCode: string
  country: string
  defaultShipping: boolean
  defaultBilling: boolean
}

export interface AddressResponse {
  id: number
  street: string
  city: string
  state: string
  postalCode: string
  country: string
  defaultShipping: boolean
  defaultBilling: boolean
}

export interface PagedAddressResponse {
  content: AddressResponse[]
  totalElements: number
  totalPages: number
  size: number
  number: number
}

export class AddressAPI {

  /**
   * Add a new address for a user
   */
  static async addAddress(
    userId: string,
    addressData: AddressRequest,
    accessToken: string
  ): Promise<AddressResponse> {
    const url = createApiUrl(`/api/v1/users/${userId}/addresses`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(addressData),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to add address: ${error}`)
    }

    return response.json()
  }

  /**
   * Get all addresses for a user (with pagination)
   */
  static async listAddresses(
    userId: string,
    accessToken: string,
    page: number = 0,
    size: number = 20
  ): Promise<PagedAddressResponse> {
    const url = createApiUrl(`/api/v1/users/${userId}/addresses?page=${page}&size=${size}`)

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to list addresses: ${error}`)
    }

    return response.json()
  }

  /**
   * Delete an address
   */
  static async deleteAddress(
    userId: string,
    addressId: number,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(`/api/v1/users/${userId}/addresses/${addressId}`)

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to delete address: ${error}`)
    }
  }

  /**
   * Set an address as default shipping
   */
  static async setDefaultShipping(
    userId: string,
    addressId: number,
    accessToken: string
  ): Promise<AddressResponse> {
    const url = createApiUrl(`/api/v1/users/${userId}/addresses/${addressId}/default-shipping`)

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to set default shipping address: ${error}`)
    }

    return response.json()
  }

  /**
   * Set an address as default billing
   */
  static async setDefaultBilling(
    userId: string,
    addressId: number,
    accessToken: string
  ): Promise<AddressResponse> {
    const url = createApiUrl(`/api/v1/users/${userId}/addresses/${addressId}/default-billing`)

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to set default billing address: ${error}`)
    }

    return response.json()
  }
}
